'use client';

import { useCallback, useRef, useState } from 'react';
import { isTurnstileEnabled, turnstileSiteKey } from 'helpers/constants/turnstile';
import { TurnstileHandle } from './index';

export type UseTurnstileReturn = {
  ref: React.RefObject<TurnstileHandle>;
  token: string;
  error: string;
  enabled: boolean;
  siteKey: string;
  handleVerify: (token: string) => void;
  handleExpireOrError: () => void;
  reset: () => void;
  validate: () => boolean;
};

// Shared across every captcha-protected form so enabling/disabling Turnstile
// (NEXT_PUBLIC_ENABLE_TURNSTILE) and its validation behave identically everywhere.
export const useTurnstile = (): UseTurnstileReturn => {
  const ref = useRef<TurnstileHandle>(null);
  const [token, setToken] = useState('');
  const [error, setError] = useState('');

  const handleVerify = useCallback((value: string) => {
    setToken(value);
    setError('');
  }, []);

  const handleExpireOrError = useCallback(() => {
    setToken('');
  }, []);

  const reset = useCallback(() => {
    ref.current?.reset();
    setToken('');
    setError('');
  }, []);

  const validate = useCallback(() => {
    if (!isTurnstileEnabled) {
      return true;
    }
    if (!token) {
      setError('Please complete the verification challenge.');
      return false;
    }
    return true;
  }, [token]);

  return {
    ref,
    token,
    error,
    enabled: isTurnstileEnabled,
    siteKey: turnstileSiteKey,
    handleVerify,
    handleExpireOrError,
    reset,
    validate,
  };
};
